import { useEffect, useState } from "react";
import StlViewer from "./components/StlViewer";
import { getPartColor, colorToCssHex } from "./components/partColors";
import "./App.css";
import logo from "./assets/inkbit-4k.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function App() {
  const [token, setToken] = useState(() => sessionStorage.getItem("token") || "");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [authMessage, setAuthMessage] = useState("");

  const [status, setStatus] = useState("idle");
  const [reloadKey, setReloadKey] = useState(0);
  const [meshUrls, setMeshUrls] = useState([]);
  const [meshParts, setMeshParts] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [jobId, setJobId] = useState("");
  const [uploadId, setUploadId] = useState("");
  const [message, setMessage] = useState("");
  const [zipFile, setZipFile] = useState(null);

  // On mount, if a token exists (e.g., after refresh), load the user.
  useEffect(() => {
    if (token) {
      fetchMe(token);
      loadJobs(token);
    }
  }, []);

  // Load jobs for user after login/refresh
  const loadJobs = async (tokenOverride) => {
    const authToken = tokenOverride || token;
    if (!authToken) return;
    try {
      const res = await authFetch(`${API_BASE}/jobs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(await parseError(res));
      const data = await res.json();
      // Only keep saved jobs; backend should already filter, but guard just in case.
      setJobs(data.filter((job) => job.saved));
    } catch (err) {
      setMessage(err.message);
    }
  };


  // Clear auth state when the token is no longer valid so the user can log back in.
  const handleSessionExpired = (note = "Session expired. Please log in again.") => {
    setAuthMessage(note);
    setMessage("");
    setToken("");
    sessionStorage.removeItem("token");
    setCurrentUser(null);
    setJobId("");
    setUploadId("");
    setMeshParts([]);
    setMeshUrls([]);
    setJobs([]);
  };

  // Helper to add token and detect expired sessions
  const authFetch = async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (res.status === 401) {
      handleSessionExpired();
      throw new Error("Session expired. Please log in again.");
    }
    return res;
  };

  // Error parsing
  async function parseError(res) {
    try {
      const data = await res.json();
      if (Array.isArray(data.detail) && data.detail.length) {
        const raw = data.detail[0].msg || data.detail[0].detail || res.statusText;
        if (raw.includes("valid email")) return "Please enter a valid email like name@example.com";
        return raw;
      }
      return data.detail || res.statusText || "Request failed";
    } catch {
      return res.statusText || "Request failed";
    }
  }

  // Fetch Job Status
  const fetchJobStatus = async (id) => {
    const res = await authFetch(`${API_BASE}/jobs/${id}`);
    if (!res.ok) throw new Error("Status check failed");
    return res.json();
  };

  const pollJob = async (id) => {
    try {
      const job = await fetchJobStatus(id);
      if (job.status === "done") {
        await loadMeshes(id);
        setStatus("succeeded");
        setMessage("Segmentation completed");
      } else if (job.status === "failed") {
        setStatus("failed");
        setMessage(job.error_message || "Segmentation failed");
      } else {
        // queued or processing
        setStatus("running");
        setTimeout(() => pollJob(id), 3000);
      }
    } catch (err) {
      setStatus("failed");
      setMessage(err.message || "Status check failed");
    }
  };


  // Upload MRI
  const handleUpload = async () => {
    if (!token) { setMessage("Please log in first"); return; }
    if (!selectedFile) return;

    // reset prior job state
    setJobId("");
    setUploadId("");
    setMeshParts([]);
    setMeshUrls([]);
    setMessage("");

    const body = new FormData();
    body.append("file", selectedFile);

    try {
      setStatus("uploading");
      const res = await authFetch(`${API_BASE}/upload`, { method: "POST", body });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      setJobId(data.job_id);
      setUploadId(data.upload_id);
      setMessage(`Uploaded ${selectedFile.name}`);
      setStatus("uploaded");
      loadJobs();
    } catch (err) {
      console.error("Upload error:", err);
      setStatus("failed");
      setMessage(err.message);
    }
  };

  // Run segmentation
  const handleGenerate = async () => {
    if (!token) { setMessage("Please log in first"); return; }
    if (!jobId || !uploadId) {
      setMessage("Upload a study first");
      return;
    }

    try {
      setStatus("running");
      const form = new FormData();
      form.append("job_id", jobId);
      form.append("upload_id", uploadId);

      const res = await authFetch(`${API_BASE}/generate`, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Generate failed (${res.status})`);
      const data = await res.json();
      setMessage("Job queued...");
      pollJob(data.job_id);
    } catch (err) {
      console.error("Generate error:", err);
      setStatus("failed");
      setMessage(err.message);
    }
  };

    

  // Upload zipped meshes
  const handleUploadZip = async () => {
    if (!token) { setMessage("Please log in first"); return; }
    if (!zipFile) return;


    const body = new FormData();
    if (jobId) {
      body.append("job_id", jobId);
    }
    body.append("zip_file", zipFile);

    try {
      const res = await authFetch(`${API_BASE}/segments/archive`, { method: "POST", body });
      if (!res.ok) throw new Error(`Zip upload failed (${res.status})`);
      const data = await res.json();
      const nextJobId = jobId || data.job_id || "";
      if (nextJobId) setJobId(nextJobId);
      setStatus("succeeded");
      await loadMeshes(nextJobId);
      loadJobs();
    } catch (err) {
      console.error("Zip upload error:", err);
      setStatus("failed");
      setMessage(err.message);
    }
  };

  // Load meshes for this job
const loadMeshes = async (targetJobId) => {
  const id = targetJobId || jobId;
  if (!id) return;
  try {
    const res = await authFetch(`${API_BASE}/segments/meshes/presigned?job_id=${id}`);
    if (!res.ok) throw new Error(`Meshes failed: ${res.status}`);
    const files = await res.json(); // [{ name, url }, ...]
    const parts = files.map(({ name, url }) => {
      const color = getPartColor(name.toLowerCase());
      return {
        name,
        label: formatPartLabel(name),
        url, // use the presigned S3 URL
        visible: true,
        color,
        cssColor: colorToCssHex(color),
      };
    });
    setMeshParts(parts);
    setStatus("succeeded");
  } catch (err) {
    console.error("Load meshes error:", err);
    setStatus("failed");
    setMessage(err.message);
  }
};

  // Keep spinal parts only
  const keepOnlySpine = async () => {
    if (!jobId) return;
    try {
      await authFetch(`${API_BASE}/segments/meshes/spine-only?job_id=${jobId}`, { method: "POST" });
      await loadMeshes();
    } catch (err) {
      console.error("Spine-only cleanup failed", err);
    }
  };

  // Reset meshes
  const resetParts = async () => {
    if (!jobId) return;
    try {
      await authFetch(`${API_BASE}/segments/meshes/reset-parts?job_id=${jobId}`, { method: "POST" });
      await loadMeshes();
    } catch (err) {
      console.error("Reset-parts cleanup failed", err);
    }
  };

  // Recompute visible mesh URLs for the viewer
  useEffect(() => {
    if (!meshParts.length) {
      setMeshUrls([]);
      return;
    }
    const visibleUrls = meshParts.filter((part) => part.visible).map((part) => part.url);
    setMeshUrls(visibleUrls);
    setReloadKey(Date.now());
  }, [meshParts]);

  // Make parts visible/invisible
  const togglePartVisibility = (partName) => {
    setMeshParts((parts) =>
      parts.map((part) => (part.name === partName ? { ...part, visible: !part.visible } : part))
    );
  };

  // Pretty part labels
  const formatPartLabel = (filename) => filename.replace(/\.stl$/i, "").replace(/_/g, " ");

  const formatTimestamp = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleString();
  };

  // Keep meshes visible as long as we have them; don't hide just because status changes.
  const hasMeshes = meshParts.length > 0;

  // Save job with a custom name so it appears in My Jobs
  const handleSaveJob = async () => {
    if (!token) { setMessage("Please log in first"); return; }
    if (!jobId) { setMessage("Generate or load a job first"); return; }
    const name = (saveName || `Job ${jobId.slice(0, 8)}`).trim();
    try {
      const res = await authFetch(`${API_BASE}/jobs/${jobId}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(await parseError(res));
      const savedJob = await res.json();
      setJobs((prev) => {
        const others = prev.filter((j) => j.job_id !== savedJob.job_id);
        return [...others, savedJob];
      });
      setMessage(`Saved as "${savedJob.name}"`);
      setSaveName("");
    } catch (err) {
      setMessage(err.message || "Save failed");
    }
  };

  // Loading screen
  function LoadingOverlay({ visible, label }) {
    if (!visible) return null;
    return (
      <div className="overlay">
        <div className="spinner" />
        <span>{label}</span>
      </div>
    );
  }

  // Download meshes (auth fetch)
  const downloadZip = async () => {
    if (!jobId) return;
    try {
      const res = await authFetch(`${API_BASE}/segments/archive.zip?job_id=${jobId}`);
      if (!res.ok) throw new Error(await parseError(res));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "segments.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(err.message || "Download failed");
    }
  };

  // Register user
  const handleRegister = async () => {
    try {
      const res = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail, password: authPassword }),
      });
      if (!res.ok) throw new Error(await parseError(res));
      setAuthMessage("Registered. Now log in.");
    } catch (err) {
      setAuthMessage(err.message || "Registration failed");
    }
  };


  // User log in
  const handleLogin = async () => {
    try {
      const form = new URLSearchParams();
      form.set("username", authEmail);
      form.set("password", authPassword);
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
      });
      if (!res.ok) throw new Error(await parseError(res));
      const data = await res.json();
      setToken(data.access_token);
      sessionStorage.setItem("token", data.access_token);
      await fetchMe(data.access_token);
      loadJobs(data.access_token);
      setAuthMessage("");
      setMessage("Logged in");
    } catch (err) {
      setAuthMessage(err.message || "Login failed");
    }
  };

  // Fetch user
  const fetchMe = async (tokenOverride) => {
    const res = await fetch(`${API_BASE}/me`, {
      headers: { Authorization: `Bearer ${tokenOverride || token}` },
    });
    if (res.status === 401) {
      handleSessionExpired();
      return;
    }
    if (res.ok) setCurrentUser(await res.json());
  };

  // Log out user
  const handleLogout = () => {
  setToken("");
  sessionStorage.removeItem("token");
  setCurrentUser(null);
  setJobId("");
  setUploadId("");
  setMeshParts([]);
  setMeshUrls([]);
  setJobs([]);
  setAuthMessage("");
  };

  /* 
    Registration Page
  */
  if (!token) {
  return (
    <div className="auth-shell">
      <header className="brand-header">
        <img src={logo} alt="Company logo" />
        <div className="brand-copy">
          <span className="brand-tagline">From concept to reality</span>
          <h1>MRI Segmentation Suite</h1>
        </div>
      </header>

      <section className="panel auth-panel">
        <h2>Register/Log in</h2>
        <input
          className="email-input"
          type="email"
          placeholder="email"
          value={authEmail}
          onChange={(e) => setAuthEmail(e.target.value)}
        />
        <input
          className="email-input"
          type="password"
          placeholder="password"
          value={authPassword}
          onChange={(e) => setAuthPassword(e.target.value)}
        />
        <div className="auth-buttons">
          <button onClick={handleRegister}>Register</button>
          <button onClick={handleLogin}>Login</button>
        </div>
        {authMessage && <div className="alert">{authMessage}</div>}
      </section>
    </div>
  );
}

/* 
  Registration Page
*/
return (
    <div className="app-shell">
      <header className="brand-header">
        <img src={logo} alt="Company logo" />
        <div className="brand-copy">
          <span className="brand-tagline">From concept to reality</span>
          <h1>MRI Segmentation Suite</h1>
        </div>
        <div className="user-pill">
          <span>{currentUser?.email}</span>
          <button type="button" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {message && <div className="alert">{message}</div>}


      <div className="app-layout">

        
        <section className="panel">

          <div className="panel-section">
            <h2>Upload MRI</h2>
            <p className="panel-subtext">Choose a DICOM zip, NRRD, MHA, or IMA file to start.</p>

            <input
              id="study-upload"
              type="file"
              accept=".nii,.nii.gz,.zip,.mha,.nrrd,.ima"
              className="file-input"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            />
            <label htmlFor="study-upload" className="file-trigger">
              {selectedFile ? selectedFile.name : "Choose MRI file…"}
            </label>

            {selectedFile && (
              <button onClick={handleUpload} disabled={status === "uploading"}>
                Upload selected file
              </button>
            )}
          </div>

          <div className="panel-section">
            <h2>Segmentation</h2>

            {uploadId && <button onClick={handleGenerate}>Generate 3D model</button>}

            {status === "succeeded" && (
              <button onClick={keepOnlySpine} disabled={status !== "succeeded"}>
                Keep spine only
              </button>
            )}

            {status === "succeeded" && (
              <button onClick={resetParts} disabled={status !== "succeeded"}>
                Reset 3D model
              </button>
            )}

            {jobId && status === "succeeded" && (
              <div className="save-row">
                <input
                  className="text-input"
                  type="text"
                  placeholder="Name this job for My Jobs"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                />
                <div className="save-actions">
                  <button type="button" onClick={handleSaveJob}>Save job</button>
                </div>
              </div>
            )}
          </div>

        <div className="panel-section">
          <h2>My Jobs</h2>
          <p className="panel-subtext">Switch between previous MRI uploads.</p>
          <div className="jobs-row">
            <select
              className="jobs-select"
              value={jobId}
              onChange={(e) => {
                const id = e.target.value;
                setJobId(id);
                setUploadId("");
                if (id) {
                  loadMeshes(id);
                  setMessage(`Loaded job ${id.slice(0, 8)}...`);
                }
              }}
              >
                <option value="">Select a job…</option>
                {jobs.map((job) => (
                  <option key={job.job_id} value={job.job_id}>
                    {(job.name || job.job_id.slice(0, 8))} created on {formatTimestamp(job.updated_at)}
                  </option>
                ))}
              </select>
              <button type="button" onClick={loadJobs}>Refresh</button>
            </div>
        </div>


          <div className="panel-section">
            <h2>Import/Export</h2>
            <p className="panel-subtext">
              Export a 3D model or choose a previous segmentation file to visualize.
              (Download may take a few seconds to start)
            </p>

            {status === "succeeded" && (
              <button type="button" onClick={downloadZip}>Download meshes (zip)</button>
            )}

            <input
              id="zip-upload"
              type="file"
              accept=".zip"
              className="file-input"
              onChange={(e) => setZipFile(e.target.files?.[0] ?? null)}
            />
            <label htmlFor="zip-upload" className="file-trigger">
              {zipFile ? zipFile.name : "Choose 3D model zip…"}
            </label>

            {zipFile && <button onClick={handleUploadZip}>Load 3D model from zip</button>}
          </div>
        </section>

        <section className="viewer-section">
          {hasMeshes ? (
            <StlViewer meshUrls={meshUrls} reloadKey={reloadKey} token={token} />
          ) : (
            <div className="viewer-placeholder">
              <p>Use the sidebar to upload an MRI. Then, click <strong>Generate 3D model</strong> and it will appear here.<br /><br />
                 You can also select a saved job or upload a previous result to visualize it.</p>
            </div>
          )}
        </section>

        {hasMeshes && (
          <aside className="parts-panel">
            <h2>Visible Parts</h2>
            <p className="panel-subtext">Tap to show or hide individual meshes.</p>
            <div className="parts-list">
              {meshParts.map((part) => (
                <button
                  key={part.name}
                  className={`part-toggle ${part.visible ? "is-visible" : "is-hidden"}`}
                  onClick={() => togglePartVisibility(part.name)}
                  style={{
                    borderColor: part.cssColor,
                    backgroundColor: `${part.cssColor}${part.visible ? "33" : "12"}`,
                    color: part.visible ? "#0f172a" : "#94a3b8",
                  }}
                >
                  {part.visible ? "Hide" : "Show"} {part.label}
                </button>
              ))}
            </div>
          </aside>
        )}

        <LoadingOverlay
          visible={status === "running"}
          label="MRI segmentation is running. This may take a minute..."
        />
      </div>
    </div>
  );
}

export default App;
